import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useActiveGroup } from "../contexts/ActiveGroupContext";
import CountdownTimer from "../components/CountdownTimer";
import RoundSelector from "../components/RoundSelector";
import MatchCard from "../components/MatchCard";
import TopRanksCard from "../components/TopRanksCard";
import MatchBetsModal from "../components/MatchBetsModal";
import UserBetsModal from "../components/UserBetsModal";
import BonusBetCard from "../components/BonusBetCard";
import { parseDate } from "../lib/utils";

export default function MyTips() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlRoundId = searchParams.get("round");
  const { activeGroup, loading: groupsLoading, setActiveGroupId } = useActiveGroup();
  const tournamentId = activeGroup?.tournament_id || null;
  const [rounds, setRounds] = useState([]);
  const [activeRound, setActiveRound] = useState(urlRoundId);
  const [matches, setMatches] = useState([]);
  const [openMatchId, setOpenMatchId] = useState(null);
  const [openUserId, setOpenUserId] = useState(null);
  const [slideDir, setSlideDir] = useState(null); // 'left' | 'right' | null
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  useEffect(() => {
    const code = localStorage.getItem('pendingInviteCode');
    if (code) {
      (async () => {
        try {
          const { data } = await api.post("/groups/join", { invite_code: code });
          localStorage.removeItem('pendingInviteCode');
          setActiveGroupId(data.group_id);
        } catch (e) {
          console.error("Failed to auto-join group", e);
          localStorage.removeItem('pendingInviteCode');
        }
      })();
    }
  }, [setActiveGroupId]);

  const [loadingRounds, setLoadingRounds] = useState(false);
  const [loadingMatches, setLoadingMatches] = useState(false);

  useEffect(() => {
    setRounds([]);
    setMatches([]);
    if (!tournamentId) return;
    (async () => {
      setLoadingRounds(true);
      try {
        const r = await api.get(`/tournaments/${tournamentId}/rounds`);
        setRounds(r.data);
      } catch (e) {
        console.error("Failed to fetch rounds", e);
      } finally {
        setLoadingRounds(false);
      }
    })();
  }, [tournamentId]);

  useEffect(() => {
    if (rounds.length === 0) {
      setActiveRound(null);
      return;
    }

    const roundExists = urlRoundId && rounds.some((round) => round.round_id === urlRoundId);
    if (roundExists) {
      if (activeRound !== urlRoundId) {
        setActiveRound(urlRoundId);
      }
    } else {
      const todayStr = new Date().toISOString().split("T")[0];
      const nextRound = rounds.find((round) => round.utc_date >= todayStr);
      const defaultRoundId = nextRound ? nextRound.round_id : rounds[rounds.length - 1].round_id;

      if (activeRound !== defaultRoundId) {
        setActiveRound(defaultRoundId);
      }

      setSearchParams(
        (prev) => {
          prev.set("round", defaultRoundId);
          return prev;
        },
        { replace: true }
      );
    }
  }, [rounds, urlRoundId, activeRound, setSearchParams]);

  const loadMatches = useCallback(async (rid, silent = false) => {
    if (!tournamentId || !rid) return;
    if (!silent) setLoadingMatches(true);
    const params = { round_id: rid };
    if (activeGroup?.group_id) {
      params.group_id = activeGroup.group_id;
    }
    const { data } = await api.get(`/tournaments/${tournamentId}/matches`, { params });
    setMatches(data);
    if (!silent) setLoadingMatches(false);
  }, [tournamentId, activeGroup?.group_id]);

  useEffect(() => {
    if (!activeRound) return;
    loadMatches(activeRound);

    const interval = setInterval(() => {
      loadMatches(activeRound, true);
    }, 30000);

    return () => clearInterval(interval);
  }, [activeRound, loadMatches]);

  const nextKickoff = useMemo(() => {
    const upcoming = matches.filter((m) => !m.locked).map((m) => parseDate(m.kickoff_at).getTime());
    if (!upcoming.length) return null;
    return new Date(Math.min(...upcoming)).toISOString();
  }, [matches]);

  const saveBet = async (matchId, home, away) => {
    await api.post("/bets", { match_id: matchId, home_score: home, away_score: away });
    // re-fetch silently in background
    loadMatches(activeRound, true);
  };

  const handleCountdownComplete = useCallback(() => {
    if (activeRound) loadMatches(activeRound);
  }, [activeRound, loadMatches]);

  const handleRoundChange = useCallback((roundId) => {
    setSearchParams((prev) => {
      prev.set("round", roundId);
      return prev;
    }, { replace: true });
  }, [setSearchParams]);

  const handleSwipe = useCallback((dir) => {
    if (!activeRound || rounds.length === 0) return;
    const idx = rounds.findIndex((r) => r.round_id === activeRound);
    if (dir === 'left' && idx < rounds.length - 1) {
      setSlideDir('left');
      setTimeout(() => {
        handleRoundChange(rounds[idx + 1].round_id);
        setSlideDir(null);
      }, 220);
    } else if (dir === 'right' && idx > 0) {
      setSlideDir('right');
      setTimeout(() => {
        handleRoundChange(rounds[idx - 1].round_id);
        setSlideDir(null);
      }, 220);
    }
  }, [activeRound, rounds, handleRoundChange]);

  const onTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const onTouchEnd = useCallback((e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;
    // Only trigger if horizontal swipe dominates and exceeds threshold
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    handleSwipe(dx < 0 ? 'left' : 'right');
  }, [handleSwipe]);

  const showLoader = loadingRounds || loadingMatches || (!activeRound && rounds.length > 0);

  const slideClass = slideDir === 'left'
    ? 'translate-x-[-18px] opacity-0'
    : slideDir === 'right'
    ? 'translate-x-[18px] opacity-0'
    : 'translate-x-0 opacity-100';

  if (!groupsLoading && !tournamentId) {
    return (
      <div className="px-4 pt-8 text-center" data-testid="my-tips-page">
        <div className="bg-white border border-neutral-200 rounded-2xl p-8">
          <h2 className="font-display text-xl text-[#02371b]">{t("no_groups_title")}</h2>
          <p className="text-sm text-neutral-500 mt-2">{t("no_groups_sub")}</p>
          <button
            type="button"
            onClick={() => navigate("/app/groups")}
            className="mt-4 bg-[#41a42b] text-white rounded-full py-2.5 px-5 text-sm font-bold"
          >
            {t("manage_groups")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="px-4 pt-4"
      data-testid="my-tips-page"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {tournamentId && (
        <TopRanksCard tournamentId={tournamentId} groupId={activeGroup?.group_id} onOpenFull={() => navigate("/app/leaderboard")} />
      )}

      <div className="mt-4">
        <CountdownTimer target={nextKickoff} onComplete={handleCountdownComplete} />
      </div>

      {tournamentId && (
        <div className="mt-4">
          <BonusBetCard tournamentId={tournamentId} />
        </div>
      )}

      {rounds.length > 0 && (
        <RoundSelector rounds={rounds} activeId={activeRound} onChange={handleRoundChange} />
      )}

      <h2 className="font-display text-2xl text-[#02371b] mb-3 mt-2" data-testid="open-matches-heading">
        {t("matches")}
      </h2>

      <div
        className={`transition-all duration-200 ease-in-out ${slideClass}`}
        style={{ willChange: 'transform, opacity' }}
      >
        {showLoader ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="bg-white rounded-2xl border border-neutral-200 p-5 animate-pulse">
                <div className="flex justify-between items-center mb-4">
                  <div className="h-4 bg-neutral-200 rounded-md w-1/4" />
                  <div className="h-4 bg-neutral-200 rounded-md w-1/4" />
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-neutral-200 rounded-full" />
                    <div className="h-4 bg-neutral-200 rounded-md w-20" />
                  </div>
                  <div className="h-6 bg-neutral-200 rounded-md w-10" />
                  <div className="flex items-center gap-2">
                    <div className="h-4 bg-neutral-200 rounded-md w-20" />
                    <div className="w-8 h-8 bg-neutral-200 rounded-full" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : matches.length === 0 ? (
          <div className="bg-white rounded-2xl border border-neutral-200 p-8 text-center text-sm text-neutral-500">
            {t("no_matches")}
          </div>
        ) : (
          matches.map((m) => (
            <MatchCard
              key={m.match_id}
              match={m}
              onSave={saveBet}
              onOpenBets={(mid) => setOpenMatchId(mid)}
            />
          ))
        )}
      </div>

      {openMatchId && (
        <MatchBetsModal
          matchId={openMatchId}
          groupId={activeGroup?.group_id}
          onClose={() => setOpenMatchId(null)}
          onUserClick={(uid) => { setOpenMatchId(null); setOpenUserId(uid); }}
        />
      )}
      {openUserId && tournamentId && (
        <UserBetsModal userId={openUserId} tournamentId={tournamentId} onClose={() => setOpenUserId(null)} />
      )}
    </div>
  );
}
